import React, { createContext, useContext, useMemo, useReducer } from 'react'

export type CartItem = {
  skillName: string
  quantity: number
  hours: number
}

type CartState = {
  items: CartItem[]
  isOpen: boolean
  isCheckoutOpen: boolean
}

type Action =
  | { type: 'add'; item: CartItem }
  | { type: 'update'; skillName: string; patch: Partial<CartItem> }
  | { type: 'remove'; skillName: string }
  | { type: 'clear' }
  | { type: 'open' }
  | { type: 'close' }
  | { type: 'openCheckout' }
  | { type: 'closeCheckout' }
  | { type: 'restore'; items: CartItem[]; openCheckout?: boolean }

const initialState: CartState = {
  items: [],
  isOpen: false,
  isCheckoutOpen: false,
}

function reducer(state: CartState, action: Action): CartState {
  switch (action.type) {
    case 'add': {
      const existing = state.items.find(
        (i) => i.skillName === action.item.skillName,
      )
      const items = existing
        ? state.items.map((i) =>
            i.skillName === action.item.skillName
              ? {
                  ...i,
                  quantity: i.quantity + action.item.quantity,
                  hours: action.item.hours,
                }
              : i,
          )
        : [...state.items, action.item]
      return { ...state, items }
    }
    case 'update':
      return {
        ...state,
        items: state.items.map((i) =>
          i.skillName === action.skillName ? { ...i, ...action.patch } : i,
        ),
      }
    case 'remove':
      return {
        ...state,
        items: state.items.filter((i) => i.skillName !== action.skillName),
      }
    case 'clear':
      return { ...state, items: [], isOpen: false, isCheckoutOpen: false }
    case 'open':
      return { ...state, isOpen: true }
    case 'close':
      return { ...state, isOpen: false }
    case 'openCheckout':
      return { ...state, isOpen: false, isCheckoutOpen: true }
    case 'closeCheckout':
      return { ...state, isCheckoutOpen: false }
    case 'restore':
      return {
        ...state,
        items: action.items,
        isCheckoutOpen: action.openCheckout ?? state.isCheckoutOpen,
        isOpen: false,
      }
  }
}

type CartContextValue = {
  items: CartItem[]
  count: number
  addItem: (item: CartItem) => void
  updateItem: (skillName: string, patch: Partial<CartItem>) => void
  removeItem: (skillName: string) => void
  clear: () => void
  isOpen: boolean
  open: () => void
  close: () => void
  isCheckoutOpen: boolean
  openCheckout: () => void
  closeCheckout: () => void
  restore: (items: CartItem[], options?: { openCheckout?: boolean }) => void
}

const CartContext = createContext<CartContextValue | null>(null)

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const value = useMemo<CartContextValue>(
    () => ({
      items: state.items,
      count: state.items.reduce((sum, i) => sum + i.quantity, 0),
      addItem: (item) => dispatch({ type: 'add', item }),
      updateItem: (skillName, patch) =>
        dispatch({ type: 'update', skillName, patch }),
      removeItem: (skillName) => dispatch({ type: 'remove', skillName }),
      clear: () => dispatch({ type: 'clear' }),
      isOpen: state.isOpen,
      open: () => dispatch({ type: 'open' }),
      close: () => dispatch({ type: 'close' }),
      isCheckoutOpen: state.isCheckoutOpen,
      openCheckout: () => dispatch({ type: 'openCheckout' }),
      closeCheckout: () => dispatch({ type: 'closeCheckout' }),
      restore: (items, options) => {
        dispatch({
          type: 'restore',
          items,
          openCheckout: options?.openCheckout,
        })
      },
    }),
    [state],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>')
  return ctx
}
